import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useTranslation } from "@/i18n/translations";

// WhatsApp connection by QR code (Green API partner instances)
export default function GreenApiConnect() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [busy, setBusy] = useState(false);
  const [startingSince, setStartingSince] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chats, setChats] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState("");
  const stateTimer = useRef(null);
  const qrTimer = useRef(null);

  const stopTimers = () => {
    clearTimeout(stateTimer.current);
    clearTimeout(qrTimer.current);
  };

  const pollQr = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/greenapi/qr`);
      if (r.data?.type === "qrCode") {
        setQr(r.data.dataUrl);
        qrTimer.current = setTimeout(pollQr, 2500);
        return;
      }
      if (r.data?.type === "alreadyLogged") {
        setQr(null);
        qrTimer.current = null; // state poll will restart QR polling if the instance is in fact not authorized
        return;
      }
    } catch (e) {
      if (e?.response?.status === 404) { qrTimer.current = null; return; }
    }
    qrTimer.current = setTimeout(pollQr, 5000);
  }, []);

  const pollState = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/greenapi/status`);
      setStatus(r.data);
      setStatusError("");
      if (!r.data?.connected) return;
      if (r.data.state === "authorized") {
        setQr(null);
        clearTimeout(qrTimer.current);
        qrTimer.current = null;
        return;
      }
      // "starting" / "notAuthorized" / unknown → keep asking for a QR; Green returns it as soon as the instance is ready
      if (!qrTimer.current) pollQr();
      stateTimer.current = setTimeout(pollState, 5000);
    } catch (e) {
      if (e?.response?.status === 503) setStatusError(e.response.data?.detail || "");
      stateTimer.current = setTimeout(pollState, 10000);
    }
  }, [pollQr]);

  useEffect(() => {
    pollState();
    return stopTimers;
  }, [pollState]);

  const isStarting = status?.connected && status?.state !== "authorized" && !qr;
  useEffect(() => {
    if (isStarting) {
      setStartingSince((prev) => prev || Date.now());
      const iv = setInterval(() => setNowTs(Date.now()), 1000);
      return () => clearInterval(iv);
    }
    setStartingSince(null);
  }, [isStarting]);
  const elapsedSec = startingSince ? Math.floor((nowTs - startingSince) / 1000) : 0;
  const elapsedFmt = `${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, "0")}`;

  const connect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/greenapi/instance`);
      toast.success(t("greenapi.toastCreated"));
      pollState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("greenapi.toastCreateFail"));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(t("greenapi.confirmDisconnect"))) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/greenapi/instance`);
      stopTimers();
      setStatus({ connected: false });
      setQr(null);
      toast.success(t("greenapi.toastDisconnected"));
    } catch (e) {
      toast.error(t("greenapi.toastDisconnectFail"));
    } finally {
      setBusy(false);
    }
  };

  const openImportDialog = async () => {
    setDialogOpen(true);
    setChats(null);
    setSearch("");
    try {
      const r = await axios.get(`${API}/greenapi/chats/preview`);
      const list = r.data?.chats || [];
      setChats(list);
      setSelected(new Set(list.filter((c) => !c.imported).map((c) => c.chat_id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("greenapi.toastImportFail"));
      setDialogOpen(false);
    }
  };

  const toggleChat = (chatId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(chatId)) next.delete(chatId);
      else next.add(chatId);
      return next;
    });
  };

  const togglePersonal = async (chat) => {
    const newVal = !chat.personal;
    try {
      await axios.post(`${API}/greenapi/chats/personal`, { chat_ids: [chat.chat_id], personal: newVal });
      setChats((prev) => prev.map((c) => (c.chat_id === chat.chat_id ? { ...c, personal: newVal } : c)));
      if (newVal) {
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(chat.chat_id);
          return next;
        });
      }
      toast.success(newVal ? t("greenapi.toastPersonalOn") : t("greenapi.toastPersonalOff"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("greenapi.toastImportFail"));
    }
  };

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    setContactsError("");
    try {
      const r = await axios.get(`${API}/greenapi/chats/preview`);
      setContacts(r.data?.chats || []);
    } catch (e) {
      setContacts([]);
      setContactsError(e?.response?.data?.detail || e.message);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status?.state === "authorized" && contacts === null) {
      loadContacts();
    }
  }, [status?.state, contacts, loadContacts]);

  const toggleContactPersonal = async (chat) => {
    const newVal = !chat.personal;
    try {
      await axios.post(`${API}/greenapi/chats/personal`, { chat_ids: [chat.chat_id], personal: newVal });
      setContacts((prev) => prev.map((c) => (c.chat_id === chat.chat_id ? { ...c, personal: newVal } : c)));
      toast.success(newVal ? t("greenapi.toastPersonalOn") : t("greenapi.toastPersonalOff"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("greenapi.toastImportFail"));
    }
  };

  const disableAllPersonal = async () => {
    const ids = (contacts || []).filter((c) => !c.personal).map((c) => c.chat_id);
    if (ids.length === 0) return;
    try {
      await axios.post(`${API}/greenapi/chats/personal`, { chat_ids: ids, personal: true });
      setContacts((prev) => prev.map((c) => ({ ...c, personal: true })));
      toast.success(t("greenapi.toastPersonalOn"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("greenapi.toastImportFail"));
    }
  };

  const enableAllPersonal = async () => {
    const ids = (contacts || []).filter((c) => c.personal).map((c) => c.chat_id);
    if (ids.length === 0) return;
    try {
      await axios.post(`${API}/greenapi/chats/personal`, { chat_ids: ids, personal: false });
      setContacts((prev) => prev.map((c) => ({ ...c, personal: false })));
      toast.success(t("greenapi.toastPersonalOff"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("greenapi.toastImportFail"));
    }
  };

  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    const s = contactSearch.trim().toLowerCase();
    if (!s) return contacts;
    return contacts.filter((c) => (c.name || "").toLowerCase().includes(s) || (c.phone || "").includes(s));
  }, [contacts, contactSearch]);

  const filtered = useMemo(() => {
    if (!chats) return [];
    const s = search.trim().toLowerCase();
    if (!s) return chats;
    return chats.filter((c) => (c.name || "").toLowerCase().includes(s) || c.phone.includes(s));
  }, [chats, search]);

  const selectAllFiltered = () => setSelected(new Set([...selected, ...filtered.map((c) => c.chat_id)]));
  const deselectAll = () => setSelected(new Set());

  const importSelected = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API}/greenapi/import-chats`, { chat_ids: [...selected] });
      toast.success(t("greenapi.toastImported", { imported: r.data.imported }));
      setDialogOpen(false);
      loadContacts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("greenapi.toastImportFail"));
    } finally {
      setImporting(false);
    }
  };

  const state = status?.state;
  const authorized = state === "authorized";

  return (
    <div className="border-2 border-black rounded-lg p-4 sm:p-6 bg-gradient-to-r from-emerald-50 to-lime-50" data-testid="greenapi-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="text-lg font-bold flex items-center gap-2 flex-wrap text-black">
            <span className="text-2xl">📲</span> {t("greenapi.title")}
            {authorized && <Badge className="bg-green-600 text-white" data-testid="greenapi-active-badge">{t("greenapi.badgeConnected")}</Badge>}
            {status?.connected && !authorized && <Badge className="bg-amber-500 text-white">{t("greenapi.badgeWaiting")}</Badge>}
            {!status?.connected && <Badge variant="outline" className="border-gray-400 text-gray-600">{t("greenapi.badgeNotConnected")}</Badge>}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t("greenapi.description")}</p>
          {authorized && status?.phone && (
            <p className="text-sm font-semibold text-green-700 mt-2" data-testid="greenapi-phone">
              {t("greenapi.connectedPhone", { phone: status.phone })}
            </p>
          )}
          {authorized && (
            <div className="mt-4 pt-4 border-t border-emerald-200" data-testid="greenapi-contacts-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <div>
                  <h4 className="font-bold text-black text-base">{t("greenapi.contactsTitle")}</h4>
                  <p className="text-xs text-gray-600">{t("greenapi.contactsHint")}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    data-testid="greenapi-contacts-refresh-btn"
                    onClick={loadContacts}
                    variant="outline"
                    size="sm"
                    disabled={contactsLoading}
                    className="border-2 border-black text-black hover:bg-emerald-100 font-semibold"
                  >
                    {contactsLoading ? "…" : `↻ ${t("greenapi.refreshChats")}`}
                  </Button>
                  <Button
                    data-testid="greenapi-import-btn"
                    onClick={openImportDialog}
                    variant="outline"
                    size="sm"
                    className="border-2 border-black text-black hover:bg-emerald-100 font-semibold"
                  >
                    ⬇ {t("greenapi.importBtn")}
                  </Button>
                </div>
              </div>
              {contactsError && (
                <p className="text-xs font-semibold text-red-700 mb-2" data-testid="greenapi-contacts-error">⚠️ {contactsError}</p>
              )}
              {contactsLoading && contacts === null ? (
                <p className="text-sm text-gray-500 py-4 text-center" data-testid="greenapi-contacts-loading">{t("greenapi.loadingChats")}</p>
              ) : (contacts && contacts.length > 0) ? (
                <div className="bg-white border border-gray-200 rounded-md">
                  <div className="flex gap-2 items-center p-2 border-b border-gray-100">
                    <Input
                      data-testid="greenapi-contacts-search"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder={t("greenapi.searchPlaceholder")}
                      className="border-2 border-black h-9 text-sm"
                    />
                    <button type="button" onClick={enableAllPersonal} data-testid="greenapi-enable-all-btn"
                      className="text-xs font-semibold underline text-green-700 whitespace-nowrap shrink-0">
                      {t("greenapi.enableAllAi")}
                    </button>
                    <button type="button" onClick={disableAllPersonal} data-testid="greenapi-disable-all-btn"
                      className="text-xs font-semibold underline text-red-600 whitespace-nowrap shrink-0">
                      {t("greenapi.disableAllAi")}
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y" data-testid="greenapi-contacts-list">
                    {filteredContacts.map((c) => (
                      <div key={c.chat_id} className="flex items-center gap-3 px-3 py-2">
                        <span className="flex-1 min-w-0">
                          <span className={`block text-sm font-medium truncate ${c.personal ? "text-gray-400 line-through" : "text-black"}`}>
                            {c.name || `+${c.phone}`}
                          </span>
                          {c.name && <span className="block text-xs text-gray-500">+{c.phone}</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleContactPersonal(c)}
                          data-testid={`greenapi-ai-toggle-${c.phone}`}
                          className={`text-xs font-semibold px-3 py-1 rounded-full border shrink-0 transition-colors ${
                            c.personal
                              ? "border-red-400 text-red-600 bg-red-50 hover:bg-red-100"
                              : "border-green-500 text-green-700 bg-green-50 hover:bg-green-100"
                          }`}
                        >
                          {c.personal ? `🔴 ${t("greenapi.aiSilent")}` : `🟢 ${t("greenapi.aiReplies")}`}
                        </button>
                      </div>
                    ))}
                    {filteredContacts.length === 0 && (
                      <p className="text-sm text-gray-500 p-4 text-center">{t("greenapi.noChats")}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-600 max-w-md" data-testid="greenapi-contacts-empty">
                  {contacts && contacts.length === 0 && !contactsError ? t("greenapi.syncingChats") : t("greenapi.importHint")}
                </p>
              )}
            </div>
          )}
          {status?.connected && !authorized && (
            <div className="mt-3">
              {qr ? (
                <div className="inline-block bg-white border-2 border-black rounded-lg p-3">
                  <img src={qr} alt="WhatsApp QR" className="w-52 h-52" data-testid="greenapi-qr-img" />
                  <p className="text-xs text-gray-600 mt-2 max-w-[210px]">{t("greenapi.qrHint")}</p>
                  <button type="button" data-testid="greenapi-qr-refresh-btn" disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try { await axios.post(`${API}/greenapi/reboot`); setQr(null); setStartingSince(Date.now()); toast.success(t("greenapi.qrRefreshing")); }
                      catch (e) { toast.error(t("greenapi.qrRefreshFailed")); } finally { setBusy(false); }
                    }}
                    className="mt-2 text-xs underline text-gray-700 hover:text-black">
                    {t("greenapi.qrNotWorking")}
                  </button>
                </div>
              ) : (
                <div className="bg-white border-2 border-black rounded-lg p-4 max-w-md" data-testid="greenapi-starting-progress">
                  <div className="space-y-2 text-sm text-black">
                    <div className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>{t("greenapi.stepCreated")}</span>
                    </div>
                    <div className="flex items-start gap-2 font-semibold">
                      <span className="mt-0.5 inline-block w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin shrink-0" />
                      <span>{t("greenapi.stepStarting")}</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-400">
                      <span>○</span>
                      <span>{t("greenapi.stepQr")}</span>
                    </div>
                  </div>
                  {startingSince && (
                    <p className="text-xs text-gray-500 mt-3" data-testid="greenapi-starting-elapsed">
                      {t("greenapi.elapsed", { time: elapsedFmt })}
                    </p>
                  )}
                  {statusError && (
                    <p className="text-xs font-semibold text-red-700 mt-2" data-testid="greenapi-provider-down">⚠️ {statusError}</p>
                  )}
                  {elapsedSec > 300 && (
                    <p className="text-xs font-semibold text-amber-700 mt-2" data-testid="greenapi-taking-long">
                      ⚠️ {t("greenapi.takingLong")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {!status?.connected && (
            <Button data-testid="greenapi-connect-btn" onClick={connect} disabled={busy}
              className="bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold">
              {busy ? "..." : t("greenapi.connectBtn")}
            </Button>
          )}
          {status?.connected && (
            <Button data-testid="greenapi-disconnect-btn" onClick={disconnect} disabled={busy}
              variant="outline" className="border-2 border-red-500 text-red-600 hover:bg-red-50">
              {t("greenapi.disconnectBtn")}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-white text-black" data-testid="greenapi-import-dialog">
          <DialogHeader>
            <DialogTitle>{t("greenapi.selectTitle")}</DialogTitle>
            <DialogDescription>{t("greenapi.selectHint")}</DialogDescription>
            <p className="text-xs text-gray-500">{t("greenapi.personalHint")}</p>
          </DialogHeader>
          {!chats ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="greenapi-chats-loading">{t("greenapi.loadingChats")}</p>
          ) : (
            <>
              <div className="flex gap-2 items-center">
                <Input
                  data-testid="greenapi-chat-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("greenapi.searchPlaceholder")}
                  className="border-2 border-black"
                />
              </div>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={selectAllFiltered} className="underline font-semibold" data-testid="greenapi-select-all">
                  {t("greenapi.selectAll")}
                </button>
                <button type="button" onClick={deselectAll} className="underline font-semibold" data-testid="greenapi-deselect-all">
                  {t("greenapi.deselectAll")}
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-md divide-y" data-testid="greenapi-chat-list">
                {filtered.length === 0 && (
                  <p className="text-sm text-gray-500 p-4 text-center">{t("greenapi.noChats")}</p>
                )}
                {filtered.map((c) => (
                  <label key={c.chat_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-emerald-50">
                    <Checkbox
                      checked={selected.has(c.chat_id)}
                      onCheckedChange={() => toggleChat(c.chat_id)}
                      data-testid={`greenapi-chat-checkbox-${c.phone}`}
                    />
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm font-medium truncate ${c.personal ? "text-gray-400" : ""}`}>{c.name || `+${c.phone}`}</span>
                      {c.name && <span className="block text-xs text-gray-500">+{c.phone}</span>}
                    </span>
                    {c.imported && (
                      <Badge variant="outline" className="text-[10px] border-emerald-500 text-emerald-700 shrink-0">
                        {t("greenapi.alreadyImported")}
                      </Badge>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); togglePersonal(c); }}
                      data-testid={`greenapi-personal-toggle-${c.phone}`}
                      className={`text-[10px] font-semibold px-2 py-1 rounded-full border shrink-0 ${
                        c.personal
                          ? "border-red-400 text-red-600 bg-red-50"
                          : "border-gray-300 text-gray-600 hover:border-black"
                      }`}
                    >
                      {c.personal ? t("greenapi.aiOff") : t("greenapi.aiOn")}
                    </button>
                  </label>
                ))}
              </div>
              <DialogFooter>
                <Button
                  data-testid="greenapi-import-selected-btn"
                  onClick={importSelected}
                  disabled={importing || selected.size === 0}
                  className="bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold w-full"
                >
                  {importing ? t("greenapi.importing") : t("greenapi.importSelected", { n: selected.size })}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
